<template>
  <div
    class="nav-menu__wrap"
    :class="[
      { 'products-section-open': showProductsSection },
      { 'desk-search-section-open': showDeskSearchSection },
    ]"
  >
    <!-- Desktop -->
    <nav class="nav-desk show-for-large">
      <NavigationMenuLinks
        class="nav-menu__links"
        :items="menuItems"
        :show-products-section="showProductsSection"
        :show-desk-search-section="showDeskSearchSection"
        @toggle-products-section="toggleProductsSection"
        @toggle-desk-search-section="toggleDeskSearchSection"
        @close-products-section="closeProductsSection"
      />
      <NavigationMenuLinks class="nav-menu__links" :links="linksMain" />
      <NuxtLink
        :to="localePath({ name: 'index' })"
        class="nav-menu__overlay__logo"
      >
        <Logo
          class="logo"
          width="200"
          height="25"
          :filled="true"
          :alt="runtimeConfig.public.siteName"
        />
      </NuxtLink>
    </nav>

    <!-- Mobile -->
    <WidgetNavigationBar :fixed="openOverlay" class="hide-for-large">
      <nav
        class="nav-menu__mobile"
        ref="menuBar"
        :class="{ '--open': openOverlay }"
      >
        <NuxtLink :to="localePath({ name: 'index' })" class="nav-menu__logo">
          <Logo
            class="logo"
            width="200"
            height="25"
            :filled="true"
            :alt="runtimeConfig.public.siteName"
          />
        </NuxtLink>

        <WidgetHamburger
          class="nav-menu__hamburger"
          @click="toggleOverlay"
          ref="hamburger"
        />
      </nav>
    </WidgetNavigationBar>
  </div>

  <!-- Desk Overlay/Submenu -->
  <NavigationProductsSection
    :show-products-section="showProductsSection"
    :navigation="navigation"
  />

  <!-- Desk Search -->
  <NavigationDeskSearchSection
    v-if="!isSmallScreen"
    :show-desk-search-section="showDeskSearchSection"
    @toggle-desk-search-section="toggleDeskSearchSection"
  />

  <!-- Overlay Mobile-->
  <WidgetOverlayFullScreen
    class="nav-menu__overlay hide-for-large"
    ref="overlay"
  >
    <div class="nav-menu__overlay__wrap">
      <NavigationMenuLinks
        class="nav-menu__links"
        :mobile-menu="mobileMenu"
        @toggle-mobile-sub-menu="toggleMobileSubMenu"
      />
    </div>
    <NavigationSubmenuMobile
      ref="secondLevelMenu"
      @goBack="goBack"
      @close-accordion="closeAccordion"
      :is-full-overlay-open="openOverlay"
      :navigation="navigation"
    />
  </WidgetOverlayFullScreen>
</template>

<script setup>
import queries from '@/graphql/craft/queries/index.js'
import { useStore } from '@/store/index.js'
import Logo from '@/assets/img/logo-bioenologia.svg'
// import LogoWhite from '@/assets/img/logo-bioenologia-white.svg'
import { navMenu } from '@/bucket/navigation.js'
import gsap from 'gsap'

/* Start Check Small Screen */
const isSmallScreen = ref(false) // Reactive variable for media query match
const updateMatch = () => {
  isSmallScreen.value = window.matchMedia('(max-width: 999px)').matches // Update reactive value
}
watch(
  () => isSmallScreen.value,
  (isSmallScreen) => {
    if (isSmallScreen && showDeskSearchSection.value) {
      showDeskSearchSection.value = false
    }
  },
)

const handleScroll = () => {
  if (showProductsSection.value) {
    showProductsSection.value = false
  }
  if (showDeskSearchSection.value) {
    showDeskSearchSection.value = false
  }
}

const handleClickOutside = (event) => {
  if (!showProductsSection.value) return

  const productsSection = document.querySelector('.products-section')
  const menuContainer = document.querySelector('.nav-menu__wrap')

  if (
    productsSection &&
    menuContainer &&
    !productsSection.contains(event.target) &&
    !menuContainer.contains(event.target)
  ) {
    showProductsSection.value = false
  }
}

onMounted(() => {
  updateMatch() // Set initial state
  window
    .matchMedia('(max-width: 999px)')
    .addEventListener('change', updateMatch)

  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window
    .matchMedia('(max-width: 999px)')
    .removeEventListener('change', updateMatch)

  // Remove scroll event listener
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('click', handleClickOutside)
})
/* End Check Small Screen */

const { configuration } = useStore()

const query = queries.navigation.getNavigation

const variables = computed(() => ({
  site: configuration.site,
}))

const { data } = await useGraphql(query, variables)

if (!data.value) {
  throw createError({ statusCode: 404 })
}

const navigation = computed(() => data.value)

const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const linksMain = navMenu.linksMain
const menuItems = navMenu.menuItems
const mobileMenu = navMenu.mobileMenu

const hamburger = ref(null)
const overlay = ref(null)
const tl = ref(null)
const secondLevelMenu = ref(null)

const openOverlay = ref(false)
const showProductsSection = ref(false)
const showDeskSearchSection = ref(false)
const showMobileSubMenu = ref(false)

const toggleProductsSection = () => {
  showProductsSection.value = !showProductsSection.value
}

const toggleDeskSearchSection = () => {
  showDeskSearchSection.value = !showDeskSearchSection.value
}

const closeProductsSection = () => {
  showProductsSection.value = false
}

const toggleMobileSubMenu = () => {
  showMobileSubMenu.value = !showMobileSubMenu.value
  if (showMobileSubMenu.value) {
    animateSecondLevelMenuIn()
  } else {
    animateSecondLevelMenuOut()
  }
}

const animateSecondLevelMenuIn = () => {
  gsap.to(secondLevelMenu.value.submenuContainer, {
    x: 0,
    duration: 0.7,
    ease: 'power2.inOut',
  })
}

const animateSecondLevelMenuOut = () => {
  gsap.to(secondLevelMenu.value.submenuContainer, {
    x: '100%',
    duration: 0.7,
    ease: 'power2.inOut',
  })
}

const goBack = () => {
  animateSecondLevelMenuOut()
  showMobileSubMenu.value = false
}

const closeAccordion = () => {
  if (secondLevelMenu.value) {
    secondLevelMenu.value.closeAccordion()
  }
}

// Gestisco Overlay
const toggleOverlay = () => {
  if (tl.value?.isActive()) {
    tl.value.progress(1)
  }
  openOverlay.value = !openOverlay.value
  setTimeout(() => {
    goBack()
  }, 700)
}
watch(
  () => openOverlay.value,
  (openOverlay) => {
    tl.value = openOverlay
      ? animateOverlayIn().duration(0.7)
      : animateOverlayOut().duration(0.7)
    tl.value.play()
  },
)
watch(
  () => route.path,
  () => {
    if (openOverlay.value) {
      toggleOverlay()
    }
    if (showProductsSection.value) {
      showProductsSection.value = false
    }
    if (showDeskSearchSection.value) {
      showDeskSearchSection.value = false
    }
  },
)

// Animazioni
const animateOverlayIn = () => {
  const tl = gsap.timeline({
    paused: true,
    onComplete: () => {
      tl.kill()
    },
  })

  if (hamburger.value) {
    tl.add(hamburger.value.animateIn().play())
  }
  if (overlay.value) {
    tl.add(overlay.value.animateIn().play(), '<')
  }
  return tl
}

const animateOverlayOut = () => {
  const tl = gsap.timeline({
    paused: true,
    onComplete: () => {
      tl.kill()
    },
  })

  if (hamburger.value) {
    tl.add(hamburger.value.animateOut().play())
  }
  if (overlay.value) {
    tl.add(overlay.value.animateOut().play(), '<')
  }

  return tl
}
</script>

<script>
export default {
  name: 'NavigationMenu',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
